import { useState } from 'react';
import type { CSSProperties, HTMLAttributes, KeyboardEvent } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { CellValue, Lesson, Subject } from '../api';
import { startCellDrag } from '../drag';
import type { CellRef } from '../drag';
import { fieldSummary } from '../fields';
import { iconFor, readableOn } from '../subjectStyle';
import { CellDetails } from './CellDetails';
import type { LessonDetails } from './CellDetails';
import { CellEditor } from './CellEditor';

export type CellMove = 'next' | 'previous' | 'up' | 'down' | 'left' | 'right';

export type CellProps = {
  /** "Ma Les 1": the accessible name, with the subject appended when there is one. */
  label: string;
  lesson: Lesson | undefined;
  subject: Subject | undefined;
  subjects: Subject[];
  mode: 'edit' | 'board';
  /** This cell's editor is open. */
  editing: boolean;
  today?: boolean;
  /** The lesson that is on right now (board). */
  running?: boolean;
  /** Plays the one bouncy moment. */
  justFilled?: boolean;
  /** Day view: details always open under the lesson instead of behind the ⋯ button. */
  detailsInline?: boolean;
  onOpen?: () => void;
  /** Saves; with a move the sheet opens the neighbouring cell. */
  onCommit?: (value: CellValue, move?: CellMove) => void;
  /** Closes the editor without saving. */
  onCancel?: () => void;
  onFields?: (values: Record<string, string>) => void;
  onDetails?: (details: LessonDetails) => void;
  /** When set (and the cell is filled) the cell can be dragged onto another cell. */
  dragRef?: CellRef;
  /** From the sheet: dragover/drop handlers. */
  dropProps?: HTMLAttributes<HTMLDivElement>;
};

/** One cell of the sheet: the subject as a colour block with its note and field rows; editable in place. */
export function Cell(props: CellProps) {
  const { label, lesson, subject, subjects, mode, editing, today, running, justFilled, detailsInline, onOpen, onCommit, onCancel, onFields, onDetails, dragRef, dropProps } = props;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const editable = mode === 'edit' && Boolean(onCommit);
  const name = subject ? `${label}: ${subject.name}` : label;
  // The colour travels as variables so the board and the print style can lighten it in CSS.
  const colour = subject ? ({ '--c': subject.color, '--c-ink': readableOn(subject.color) } as CSSProperties) : undefined;
  const hasDetails = Boolean(lesson?.goal || lesson?.materials);
  // A week cell has no room for the series of every field: one line, the full series lives in the ⋯ popover and the day view.
  const summary = fieldSummary(subject, lesson?.fieldValues);
  const hasFields = (subject?.fields.length ?? 0) > 0;
  const showMore = Boolean(subject && !editing && !detailsInline && (onDetails || onFields));

  function keyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Keys typed in the popover or on a series button bubble up through React; only the cell itself opens.
    if (!editable || editing || event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen?.();
    }
  }

  const body = (
    <>
      {subject && (
        <span className={`block break-words font-semibold leading-tight hyphens-auto ${showMore ? 'pr-6' : ''}`}>
          <span aria-hidden="true">{iconFor(subject.icon)} </span>
          {subject.name}
        </span>
      )}
      {editing && onCommit && onCancel ? (
        <CellEditor subjects={subjects} initial={{ subjectId: lesson?.subjectId ?? null, note: lesson?.note ?? '' }} onCommit={onCommit} onCancel={onCancel} />
      ) : (
        lesson?.note && <span className="block text-[0.9em] leading-snug">{lesson.note}</span>
      )}
      {!detailsInline && summary && <span className="block text-[0.85em] leading-snug opacity-90">{summary}</span>}
      {detailsInline && subject && (
        // Typing in the details must not open the cell editor behind it.
        <div onClick={(event) => event.stopPropagation()}>
          <CellDetails subject={subject} lesson={lesson} {...(onDetails ? { onDetails } : {})} {...(onFields ? { onFields } : {})} />
        </div>
      )}
    </>
  );

  const className = [
    'cell group relative flex min-h-14 flex-col gap-1 rounded-md px-2 py-1.5 text-left',
    subject ? 'cell-filled' : 'cell-empty',
    today ? 'cell-today' : '',
    running ? 'cell-running' : '',
    justFilled ? 'cell-pop' : '',
    editable ? 'cursor-text' : '',
  ].join(' ');

  if (!editable) {
    return (
      <div role="gridcell" aria-label={name} className={className} style={colour}>
        {body}
      </div>
    );
  }

  return (
    <div
      role="gridcell"
      aria-label={name}
      aria-selected={editing || undefined}
      tabIndex={editing ? -1 : 0}
      className={className}
      style={colour}
      onClick={() => !editing && onOpen?.()}
      onKeyDown={keyDown}
      draggable={Boolean(dragRef && subject && !editing && !detailsInline)}
      onDragStart={(event) => dragRef && startCellDrag(event, dragRef)}
      {...dropProps}
    >
      {body}
      {showMore && (
        <Popover open={detailsOpen} onOpenChange={setDetailsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Meer over ${label}`}
              onClick={(event) => event.stopPropagation()}
              className={`absolute right-0.5 top-0.5 h-7 w-7 ${hasDetails || hasFields ? '' : 'opacity-60 group-hover:opacity-100 group-focus-within:opacity-100'}`}
              style={{ color: 'inherit' }}
            >
              <MoreHorizontal />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80" onClick={(event) => event.stopPropagation()}>
            <CellDetails key={lesson?.subjectId ?? ''} subject={subject} lesson={lesson} {...(onDetails ? { onDetails } : {})} {...(onFields ? { onFields } : {})} />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
